<template>
    <div class="">
        <!-- DataTable Component -->
        <DataTable
            :perPage="itemsPerPage"
            :columns="visibleColumns"
            :items="tableData"
            :currentPage="currentPage"
            :totalPages="totalPages"
            :selectedItems="selectedItems"
            :enableDragDrop="false"
            :enableMultiSelect="true"
            rowClass="text-center"
            :sortBy="sortColumn"
            :sortOrder="sortDirection"
            @page-changed="handlePageChange"
            @item-selected="toggleItemSelection"
            @select-all-toggled="toggleSelectAll"
            @sort="handleSort"
        >
            <!-- Toolbar Slot -->
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="search" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                    </div>

                    <!-- Export Button -->
                    <ExportSelect
                        :label="$t('groups.export')"
                        :options="[
                            { label: $t('groups.csv'), value: 'csv' },
                            { label: $t('groups.excel'), value: 'excel' },
                        ]"
                        :disabled="exportLoading"
                        :loading="exportLoading"
                        @export-selected="downloadGroups"
                    />
                    <div class="ppv2-flex-1"></div>

                    <BaseButton
                        variant="purple"
                        :custom-height="40"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="openCreateModal"
                    >
                        <Plus :size="17" />
                        <span class="poppinsRegular"> {{ $t('groups.add') }}</span>
                    </BaseButton>
                </div>
            </template>

            <!-- Column: Statut (status_id) -->
            <template #column-status_id="{ item }">
                <div
                    class="ppv2-inline-flex ppv2-min-h-[28px] ppv2-items-center ppv2-justify-center ppv2-gap-2 ppv2-px-3 ppv2-rounded-xl ppv2-transition-all"
                    :class="getStatusBadgeClass(item.status_id)"
                >
                    <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{ item.status_id === 1 ? $t('groups.active') : $t('groups.inactive') }}
                    </span>
                </div>
            </template>

            <!-- Column: Nom (name) -->
            <template #column-name="{ item }">
                <span
                    class="ppv2-text-black poppinsRegular ppv2-underline ppv2-cursor-pointer"
                    :style="{ fontSize: normalizeFontSize(14) }"
                >
                    {{ item.name }}
                </span>
            </template>

            <!-- Column: Propriétaire (owner) -->
            <template #column-owner="{ item }">
                <div class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    <!-- Gestion du cas où owner est null ou owner.user existe -->
                    <span v-if="item.owner && item.owner.user">
                        {{ item.owner.user.first_name }} {{ item.owner.user.last_name }}
                    </span>
                    <span v-else class="ppv2-italic ppv2-opacity-50"> - </span>
                </div>
            </template>

            <!-- Column: Nbr. de clients (customers_count) -->
            <template #column-customers_count="{ item }">
                <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ item.customers_count || 0 }}
                </span>
            </template>

            <!-- Column: Actions -->
            <template #column-actions="{ item }">
                <div class="ppv2-relative ppv2-flex ppv2-items-center ppv2-justify-center">
                    <button
                        @click.stop="toggleActionMenu(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :ref="`actionButton-${item.id}`"
                    >
                        <MoreVertical :size="18" />
                    </button>

                    <!-- Actions Dropdown Menu -->
                    <div
                        v-if="activeActionMenu === item.id"
                        class="actionsDropdown ppv2-fixed ppv2-rounded-xl ppv2-overflow-hidden ppv2-z-[100] ppv2-min-w-[220px] ppv2-shadow-lg"
                        :style="dropdownPosition"
                        :ref="`actionMenu-${item.id}`"
                    >
                        <!-- Modifier -->
                        <button
                            @click="openEditModal(item.id)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <SquarePen :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('groups.edit')
                            }}</span>
                        </button>

                        <!-- Activer / Désactiver -->
                        <button
                            v-if="item.status_id === 1"
                            @click="handleDisable(item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <Ban :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('groups.disable')
                            }}</span>
                        </button>
                        <button
                            v-else
                            @click="handleEnable(item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors"
                        >
                            <CheckCircle :size="16" />
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('groups.enable')
                            }}</span>
                        </button>

                        <!-- Supprimer -->
                        <button
                            @click="deleteSingleItem(item)"
                            class="actionItem ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-cursor-pointer ppv2-transition-colors ppv2-text-red-500 hover:ppv2-bg-red-50"
                        >
                            <Trash2 :size="16" class="ppv2-text-red-500" />
                            <span class="poppinsRegular ppv2-text-red-500" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('groups.delete')
                            }}</span>
                        </button>
                    </div>
                </div>
            </template>

            <!-- Bulk Actions Slot -->
            <template #bulk-actions="{ selectedCount }">
                <span :style="{ fontSize: normalizeFontSize(15) }" class="poppinsRegular textGray">
                    {{ $t('groups.selectedItems', { count: selectedCount, plural: selectedCount > 1 ? 's' : '' }) }}
                </span>
                <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                    >|</span
                >
                <BaseButton
                    variant="outline"
                    @click="deleteSelectedItems"
                    customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                >
                    <Trash2 :size="14" />
                    <span>{{ $t('common.deleteSelection') }}</span>
                </BaseButton>
            </template>
        </DataTable>
        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('groups.delete') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Disable Confirmation Popup -->
        <CustomDialog
            v-model="showDisablePopup"
            :defaultTitle="$t('groups.disableConfirmTitle')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="showDisablePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="disable"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('groups.disable') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Enable Confirmation Popup -->
        <CustomDialog
            v-model="showEnablePopup"
            :defaultTitle="$t('groups.enableConfirmTitle')"
            heightClass="ppv2-max-h-[40vh]"
            customClassTitle="poppinsSemiBold"
        >
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="showEnablePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="primary"
                    @click="enable"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px] !ppv2-bg-[#7D2DBD]`"
                    :customHeight="45"
                >
                    {{ $t('groups.enable') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Group Creation/Edit Modal -->
        <GroupModal
            v-model="isModalOpen"
            :isEditMode="isEditMode"
            :isSaving="isSaving"
            :groupForm="groupForm"
            :customers="customers"
            :errors="formErrors"
            @save="saveGroup"
        />
    </div>
</template>

<script>
import { SquarePen, Trash2, Plus, MoreVertical, Ban, CheckCircle } from 'lucide-vue'
import { eventBus } from '../../../Customer/eventBus'
import DataTable from '../../../Shared/DataTable.vue'
import Search from '../../Experience/components/Search.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import ExportSelect from '../../../Shared/ExportSelect.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import GroupModal from './GroupModal.vue'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../../utils/perPage'

export default {
    name: 'GroupTable',
    components: {
        SquarePen,
        Trash2,
        Plus,
        MoreVertical,
        Ban,
        CheckCircle,
        DataTable,
        Search,
        BaseButton,
        CustomDialog,
        ExportSelect,
        GroupModal,
    },
    props: {
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            search: '',
            tableData: [],
            itemsPerPage: getPerPage('groups_items_per_page', 10),
            recordsTotal: 0,
            currentPage: 1,
            loading: false,
            exportLoading: false,
            selectedItems: [],
            selectAll: false,
            showDeletePopup: false,
            deletePopupTitle: this.$t('groups.deletePopupTitle'),
            deletePopupDescription: '',
            deleteAction: null,
            itemToDelete: null,
            activeActionMenu: null,
            dropdownPosition: { top: '0px', left: '0px' },
            sortColumn: 'created_at',
            sortDirection: 'desc',

            // Enable/Disable popups
            showDisablePopup: false,
            showEnablePopup: false,
            itemToToggle: null,

            // --- C'est ici qu'on définit les colonnes par défaut ---
            selectedColumnKeys: ['name', 'status_id', 'owner', 'customers_count', 'actions'],

            // Modal State
            isModalOpen: false,
            isEditMode: false,
            isSaving: false,
            customers: [],
            formErrors: {},
            groupForm: {
                id: null,
                name: '',
                owner_id: null,
                customer_ids: [],
                active: true,
            },
        }
    },
    computed: {
        // --- Définition stricte des colonnes demandées ---
        allColumns() {
            return [
                { key: 'name', label: this.$t('groups.name'), sortable: true, type: 'text' },
                { key: 'status_id', label: this.$t('groups.status'), sortable: true, type: 'badge' },
                { key: 'owner', label: this.$t('groups.owner'), sortable: true, type: 'text' },
                {
                    key: 'customers_count',
                    label: this.$t('groups.customersCount'),
                    sortable: true,
                    type: 'number',
                },
                { key: 'actions', label: this.$t('groups.actions'), sortable: false, type: 'actions' },
            ]
        },
        totalPages() {
            return Math.ceil(this.recordsTotal / this.itemsPerPage)
        },
        visibleColumns() {
            return this.allColumns.filter((column) => this.selectedColumnKeys.includes(column.key))
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getStatusBadgeClass(statusId) {
            if (statusId === 1) {
                return 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
            } else {
                return 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            const params = this.getParams()
            this.fetchData(params)
        },
        handlePerPageChange(perPage) {
            this.itemsPerPage = Number(perPage)
            setPerPage('groups_items_per_page', this.itemsPerPage)
            const params = this.getParams()
            this.fetchData(params)
        },
        handleSearchUpdate(searchValue) {
            this.search = searchValue
            this.currentPage = 1
            const params = this.getParams()
            this.fetchData(params)
        },
        handleSort(key) {
            if (this.sortColumn === key) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortColumn = key
                this.sortDirection = 'asc'
            }
            const params = this.getParams()
            this.fetchData(params)
        },
        handleClickOutside(event) {
            if (this.activeActionMenu) {
                const actionButton = this.$refs[`actionButton-${this.activeActionMenu}`]
                const actionMenu = this.$refs[`actionMenu-${this.activeActionMenu}`]

                const btnEl = Array.isArray(actionButton) ? actionButton[0] : actionButton
                const menuEl = Array.isArray(actionMenu) ? actionMenu[0] : actionMenu

                const clickedButton = btnEl && (btnEl === event.target || btnEl.contains(event.target))
                const clickedMenu = menuEl && (menuEl === event.target || menuEl.contains(event.target))

                if (!clickedButton && !clickedMenu) {
                    this.activeActionMenu = null
                }
            }
        },
        toggleActionMenu(itemId) {
            if (this.activeActionMenu === itemId) {
                this.activeActionMenu = null
            } else {
                this.activeActionMenu = itemId
                this.$nextTick(() => {
                    this.calculateDropdownPosition(itemId)
                })
            }
        },

        calculateDropdownPosition(itemId) {
            const button = this.$refs[`actionButton-${itemId}`]
            const btnEl = Array.isArray(button) ? button[0] : button
            if (!btnEl) return

            const rect = btnEl.getBoundingClientRect()
            this.dropdownPosition = {
                top: `${rect.bottom + 4}px`,
                left: `${rect.right - 220}px`, // 220 is min-width of dropdown
            }
        },

        updateDropdownPosition() {
            if (this.activeActionMenu) {
                this.calculateDropdownPosition(this.activeActionMenu)
            }
        },
        getParams() {
            const params = {
                search: this.search,
                currentPage: this.currentPage,
                itemsPerPage: this.itemsPerPage,
            }
            if (this.sortColumn) {
                params.sortBy = this.sortColumn
                params.sortDirection = this.sortDirection
            }
            return params
        },
        async fetchData(params) {
            this.loading = true
            try {
                // Appel API vers les groupes
                const response = await axios.get('/api/v3/group/list', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                const data = response.data

                this.recordsTotal = data.total
                this.tableData = data.data || []
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },

        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                this.selectedItems.splice(index, 1)
            } else {
                this.selectedItems.push(itemId)
            }
            this.selectAll = this.selectedItems.length === this.tableData.length
        },
        toggleSelectAll() {
            const allIds = this.tableData.map((item) => item.id)
            const allSelected = allIds.length > 0 && allIds.every((id) => this.selectedItems.includes(id))
            if (allSelected) {
                this.selectedItems = []
            } else {
                this.selectedItems = [...allIds]
            }
        },
        deleteSingleItem(item) {
            this.deleteAction = 'single'
            this.itemToDelete = item
            this.deletePopupDescription = this.$t('groups.deletePopupDescriptionSingle')
            this.showDeletePopup = true
        },
        async deleteSelectedItems() {
            if (this.selectedItems.length === 0) return
            this.deleteAction = 'multiple'
            this.deletePopupDescription = this.$t('groups.deletePopupDescriptionMultiple', { count: this.selectedItems.length })
            this.showDeletePopup = true
        },
        async confirmDelete() {
            const ids = this.deleteAction === 'single' ? [this.itemToDelete.id] : this.selectedItems
            try {
                await axios.post(
                    '/api/v3/group/delete',
                    { ids },
                    {
                        headers: { Authorization: `Bearer ${this.token}` },
                    }
                )
                this.$toast?.success?.(this.$t('groups.deleteSuccess'))
                this.showDeletePopup = false
                this.selectedItems = []
                this.fetchData(this.getParams())
                this.$emit('refresh-stats')
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleDisable(item) {
            this.itemToToggle = item
            this.showDisablePopup = true
        },
        async disable() {
            try {
                await axios.post(
                    `/api/v3/group/disable/${this.itemToToggle.id}`,
                    {},
                    {
                        headers: { Authorization: `Bearer ${this.token}` },
                    }
                )
                this.$toast?.success?.(this.$t('groups.disableSuccess'))
                this.showDisablePopup = false
                this.fetchData(this.getParams())
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleEnable(item) {
            this.itemToToggle = item
            this.showEnablePopup = true
        },
        async enable() {
            try {
                await axios.post(
                    `/api/v3/group/enable/${this.itemToToggle.id}`,
                    {},
                    {
                        headers: { Authorization: `Bearer ${this.token}` },
                    }
                )
                this.$toast?.success?.(this.$t('groups.enableSuccess'))
                this.showEnablePopup = false
                this.fetchData(this.getParams())
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        async downloadGroups(format = 'csv') {
            this.exportLoading = true
            try {
                const params = this.getParams()
                params.format = format
                const response = await axios.get('/api/v3/group/export', {
                    params,
                    headers: { Authorization: `Bearer ${this.token}` },
                    responseType: 'blob',
                })

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', `groups-${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
                this.$toast?.success?.(this.$t('groups.exportSuccess'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.exportLoading = false
            }
        },

        // Modal Methods
        async fetchCustomers() {
            try {
                const res = await axios.get('/api/v3/customer/fast-list', {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.customers = res.data.data || []
            } catch (e) {
                console.error('Error fetching customers:', e)
            }
        },
        openCreateModal() {
            this.isEditMode = false
            this.groupForm = {
                id: null,
                name: '',
                owner_id: null,
                customer_ids: [],
                active: true,
            }
            this.formErrors = {}
            this.fetchCustomers()
            this.isModalOpen = true
        },
        async openEditModal(id) {
            this.activeActionMenu = null
            try {
                const res = await axios.get(`/api/v3/group/show/${id}`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                const data = res.data.data
                this.groupForm = {
                    id: data.id,
                    name: data.name,
                    owner_id: data.owner_id,
                    customer_ids: data.customer_ids,
                    active: data.status_id === 1,
                }
                this.isEditMode = true
                this.formErrors = {}
                await this.fetchCustomers()
                this.isModalOpen = true
            } catch (e) {
                this.$toast.error(e.message)
            }
        },
        async saveGroup() {
            this.isSaving = true
            this.formErrors = {}
            try {
                const url = this.isEditMode ? `/api/v3/group/update/${this.groupForm.id}` : '/api/v3/group/store'
                const payload = {
                    ...this.groupForm,
                    status_id: this.groupForm.active ? 1 : 2,
                }

                await axios.post(url, payload, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })

                this.$toast?.success?.(this.isEditMode ? 'Groupe mis à jour' : 'Groupe créé')
                this.isModalOpen = false
                this.fetchData(this.getParams())
                this.$emit('refresh-stats')
            } catch (e) {
                if (e.response && e.response.status === 422) {
                    this.formErrors = e.response.data.errors
                } else {
                    console.error('Error saving group:', e)
                    this.$toast?.error?.("Une erreur est survenue lors de l'enregistrement")
                }
            } finally {
                this.isSaving = false
            }
        },
    },
    mounted() {
        const params = this.getParams()
        this.fetchData(params)
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        document.addEventListener('click', this.handleClickOutside)
        window.addEventListener('scroll', this.updateDropdownPosition, true)
        window.addEventListener('resize', this.updateDropdownPosition)
    },
    beforeDestroy() {
        eventBus.$off('dt-selected-perpage-changed', this.handlePerPageChange)
        document.removeEventListener('click', this.handleClickOutside)
        window.removeEventListener('scroll', this.updateDropdownPosition, true)
        window.removeEventListener('resize', this.updateDropdownPosition)
    },
    watch: {
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.tableData.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },
        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.menuDropdown {
    background: white;
    color: #94a3b8;
    margin-top: 10px;
    z-index: 50;
    padding: 8px 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdownItem:hover {
    background-color: #1a1f37;
    color: white !important;
}

.dropdownItem:hover * {
    color: white !important;
}

.actionsDropdown {
    position: fixed;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 4px 0;
}

.actionItem {
    background: transparent;
    border: none;
    color: #1a1f37;
    text-decoration: none;
    display: flex;
    align-items: center;
}

.actionItem:hover {
    background-color: #f3f4f6;
    color: #1a1f37 !important;
}

.actionItem:hover * {
    color: #1a1f37 !important;
}

.dark {
    .menuDropdown {
        background-color: #2c2c2ce5;
    }

    .actionsDropdown {
        background-color: #2c2c2ce5;
    }

    .actionItem {
        color: #bfbfbf;
    }

    .actionItem:hover {
        background-color: #1a1f37;
        color: white !important;
    }

    .actionItem:hover * {
        color: white !important;
    }
}

.textGray {
    color: #1a1f37;
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}

.vue-dialog {
    font-size: clamp(12px, 0.729545vw, 16px);
    width: 100% !important;
    padding-bottom: 35px;
    text-align: center;
    max-width: 600px;
    left: 0 !important;
    margin: auto;
}

.vue-dialog-button b {
    line-height: 44px;
}

.vue-dialog-buttons {
    border-top: none !important;
    gap: 20px;
}

.vue-dialog-button:not(:first-of-type) {
    border-left: none !important;
}

.vue-dialog-content-title {
    min-height: 1.75rem;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.75rem;
    border-bottom: 1px solid #eee;
}

.vue-dialog-content {
    padding: 14px 0;
}

@media (max-width: 767.98px) {
    .vue-dialog-button b {
        line-height: 30px;
    }
}
</style>
